import { useSelector } from "react-redux"

const AREA_ORDER = ["librarian", "analysis", "project", "admin"]

const AREA_TITLES = new Map([
  ["librarian", "Librarian"],
  ["analysis", "Analysis"],
  ["project", "Projects"],
  ["admin", "Administration"],
])

export function areaTitle(area) {
  return AREA_TITLES.get(area) || area
}

let sitemapPromise = null
const pageCache = new Map()

export function loadSitemap() {
  if (!sitemapPromise) {
    sitemapPromise = fetch("/help/sitemap.json").then((r) => {
      if (!r.ok) throw new Error(`Failed to load sitemap (${r.status})`)
      return r.json()
    })
  }
  return sitemapPromise
}

export function loadPage(area, slug) {
  const key = `${area}/${slug}`
  if (pageCache.has(key)) return Promise.resolve(pageCache.get(key))
  return fetch(`/help/${encodeURIComponent(area)}/${encodeURIComponent(slug)}.md`).then((r) => {
    if (!r.ok) throw new Error(`Failed to load page ${key} (${r.status})`)
    return r.text().then((md) => {
      const stripped = stripFrontmatter(md)
      pageCache.set(key, stripped)
      return stripped
    })
  })
}

function stripFrontmatter(md) {
  return md.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/, "")
}

export function rewriteImagePath(src, area, _slug) {
  if (!src) return src
  if (/^(?:https?:|data:|blob:|\/)/i.test(src)) return src
  // Markdown writes images as ./images/foo.png relative to the .md file at
  // public/help/<area>/intro.md. The actual files now live at
  // public/help/<area>/images/foo.png so the URL only needs the area prefix.
  // This layout also matches what VS Code's markdown preview resolves on disk.
  const cleaned = src.replace(/^\.\//, "")
  return `/help/${encodeURIComponent(area)}/${cleaned}`
}

export function deriveAvailableAreas(authState, teamStatusState) {
  const role = authState?.role
  const isOnlyLibrarian = Boolean(authState?.isOnlyLibrarian)
  const onlyLibrarian = Boolean(teamStatusState?.onlyLibrarian)
  const showLibrarian = Boolean(teamStatusState?.showLibrarian)
  const showAnalyses = Boolean(teamStatusState?.showAnalyses)
  const showProjects = Boolean(teamStatusState?.showProjects)

  if (role === "admin" || role === "customer_admin") return ["admin"]
  if (isOnlyLibrarian || onlyLibrarian) return ["librarian"]

  const areas = []
  if (showLibrarian) areas.push("librarian")
  if (showAnalyses) areas.push("analysis")
  if (showProjects) areas.push("project")
  return areas
}

export function useAvailableAreas() {
  return useSelector((state) =>
    deriveAvailableAreas(state.auth, state.teamStatus)
  )
}

export function groupSitemapByArea(sitemap, availableAreas) {
  const groups = new Map()
  for (const area of availableAreas) groups.set(area, [])
  for (const entry of sitemap) {
    if (groups.has(entry.area)) groups.get(entry.area).push(entry)
  }
  return AREA_ORDER.filter((a) => groups.has(a)).map((area) => ({
    area,
    title: areaTitle(area),
    pages: groups.get(area),
  }))
}

export function findFirstPage(sitemap, area) {
  return sitemap.find((entry) => entry.area === area) || null
}
